import { publisherApi } from '@/shared/api/publisherApi';
import { ErrorResponse } from '@/shared/api/types';

import {
  AdUnit,
  AdUnitsResponseDto,
  AdUnitsSearchResponse,
  CreateAdUnitParams,
  CreateSiteV3Arg,
  DeleteAdUnitArg,
  EditAdUnitArg,
  SitesV3ResponseDto,
  SiteV3,
} from './types';

export const sitesApi = publisherApi.injectEndpoints({
  endpoints: (build) => ({
    getSitesV3: build.query<SitesV3ResponseDto['data'], void>({
      query: () => {
        const url = `/v3/sites`;

        return {
          url,
          params: {
            page: 1,
            limit: 1000,
          },
        };
      },
      transformResponse: (response: SitesV3ResponseDto) =>
        response.data.filter(({ status }) => status !== 3),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'SitesV3', id }) as const),
              { type: 'SitesV3', id: 'LIST' },
            ]
          : [{ type: 'SitesV3', id: 'LIST' }],
    }),
    createSiteV3: build.mutation<SiteV3, CreateSiteV3Arg>({
      query: (patch) => ({
        url: `/v3/sites`,
        method: 'POST',
        body: patch,
      }),
      invalidatesTags: [{ type: 'SitesV3', id: 'LIST' }],
      transformErrorResponse: (response: ErrorResponse) =>
        response.data.message,
    }),
    deleteSiteV3: build.mutation<void, number>({
      query: (id) => {
        const url = `/v3/sites/${id}`;

        return { url, method: 'DELETE' };
      },

      invalidatesTags: () => ['SitesV3'],
    }),

    // AdUnits:
    createAdUnitV3: build.mutation<
      AdUnit,
      { siteId: SiteV3['id'] } & CreateAdUnitParams
    >({
      query: ({ siteId, ...body }) => ({
        url: `/v3/sites/${siteId}/ad-units`,
        method: 'POST',
        body,
      }),
      invalidatesTags: [{ type: 'AdUnits', id: 'LIST' }],
      transformErrorResponse: (response: ErrorResponse) =>
        response.data.message,
    }),

    getSiteAdUnits: build.query<AdUnitsResponseDto, { siteId: number }>({
      query: ({ siteId }) => {
        const url = `/v3/sites/${siteId}/ad-units`;

        return {
          url,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(
                ({ id }) => ({ type: 'AdUnits', id }) as const
              ),
              { type: 'AdUnits', id: 'LIST' },
            ]
          : [{ type: 'AdUnits', id: 'LIST' }],
    }),

    getAdUnitsSearch: build.query<AdUnitsSearchResponse['data'], void>({
      query: () => {
        const url = 'v3/ad-units/search';

        return {
          url,
        };
      },
      transformResponse: (response: AdUnitsSearchResponse) => response.data,
    }),

    updateAdUnitV3: build.mutation<
      AdUnit,
      { adUnitId: AdUnit['id'] } & EditAdUnitArg
    >({
      query: ({ adUnitId, ...body }) => ({
        url: `/v3/ad-units/${adUnitId}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: (_, __, { adUnitId }) => [
        { type: 'AdUnits', id: adUnitId },
      ],
    }),

    deleteAdUnitV3: build.mutation<void, DeleteAdUnitArg>({
      query: ({ siteId, adUnitId }) => {
        const url = `/v3/sites/${siteId}/ad-units/${adUnitId}`;

        return { url, method: 'DELETE' };
      },

      invalidatesTags: () => ['AdUnits'],
    }),
  }),
});

export const {
  useCreateSiteV3Mutation,
  useGetSitesV3Query,
  useDeleteSiteV3Mutation,

  useCreateAdUnitV3Mutation,
  useGetSiteAdUnitsQuery,
  useDeleteAdUnitV3Mutation,
  useUpdateAdUnitV3Mutation,

  useGetAdUnitsSearchQuery,
} = sitesApi;
